// pages/EmployeeManagment.jsx
import React, { useEffect, useMemo, useState } from "react"
import {
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
  Spin,
} from "antd"
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons"
import { doc, onSnapshot, updateDoc } from "firebase/firestore"
import { db } from "../../../firebase/config"

const { Text, Title } = Typography

const MACHINES = ["Arsoma", "Winder", "Cutter", "Viva"]

// Turn "Aleksejs + Jurijs" -> "AleksejsAndJurijs"
function camelKeyFromLabel(label = "") {
  const words = String(label)
    .trim()
    .split(/[^a-zA-Z0-9]+/g)
    .filter(Boolean)
  if (words.length === 0) return ""
  return words
    .map((w, i) =>
      i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join("")
}

// Normalize doc field value to [{label, value}]
function normalizeList(arr) {
  if (!Array.isArray(arr)) return []
  return arr.map((item) =>
    typeof item === "string"
      ? { label: item, value: item }
      : { label: item.label ?? item.value, value: item.value ?? item.label }
  )
}

export default function EmployeeManagment() {
  const [activeMachine, setActiveMachine] = useState(MACHINES[0])
  const [docData, setDocData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => {
    setLoading(true)
    const ref = doc(db, "settings", "employeeList")
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          // create an empty doc shape lazily (optional)
          const empty = MACHINES.reduce((acc, m) => ({ ...acc, [m]: [] }), {})
          setDocData(empty)
          setLoading(false)
          return
        }
        setDocData(snap.data() || {})
        setLoading(false)
      },
      (err) => {
        setError(err)
        setLoading(false)
      }
    )
    return () => unsub()
  }, [])

  const employees = useMemo(() => normalizeList(docData?.[activeMachine]), [docData, activeMachine])

  // open modal to add new
  const openAdd = () => {
    setEditingIndex(null)
    form.resetFields()
    setModalOpen(true)
  }

  // open modal to edit existing
  const openEdit = (idx) => {
    const item = employees[idx]
    setEditingIndex(idx)
    form.setFieldsValue({ label: item.label, value: item.value })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingIndex(null)
    form.resetFields()
  }

  // save add/edit
  const onSubmit = async () => {
    try {
      const { label, value } = await form.validateFields()
      const newLabel = label.trim()
      const newValue = value.trim()

      if (!newLabel || !newValue) {
        message.error("Label and Value are required.")
        return
      }

      // prevent duplicates by value (or label)
      const dupe = employees.some((e, idx) => e.value === newValue && idx !== editingIndex)
      if (dupe) {
        message.error("An employee with the same value already exists.")
        return
      }

      const updated = [...employees]
      if (editingIndex == null) {
        updated.push({ label: newLabel, value: newValue })
      } else {
        updated[editingIndex] = { label: newLabel, value: newValue }
      }

      setSaving(true)
      const ref = doc(db, "settings", "employeeList")
      await updateDoc(ref, { [activeMachine]: updated })
      setSaving(false)
      message.success(editingIndex == null ? "Employee added." : "Employee updated.")
      closeModal()
    } catch (err) {
      if (err?.name === "Error") {
        // validation error already shown
        return
      }
      console.error(err)
      setSaving(false)
      message.error(err?.message || "Failed to save.")
    }
  }

  // delete
  const onDelete = async (idx) => {
    try {
      const updated = employees.filter((_, i) => i !== idx)
      setSaving(true)
      const ref = doc(db, "settings", "employeeList")
      await updateDoc(ref, { [activeMachine]: updated })
      setSaving(false)
      message.success("Employee deleted.")
    } catch (err) {
      console.error(err)
      setSaving(false)
      message.error(err?.message || "Failed to delete.")
    }
  }

  const columns = [
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      render: (v) => <Text>{v}</Text>,
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, __, idx) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(idx)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete employee?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(idx)}>
            <Button danger size="small" icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={3} style={{ marginBottom: 0 }}>
            Employee Management
          </Title>
          <Text type="secondary">Manage printer/operator lists per machine</Text>
          <Divider />
        </Col>

        <Col span={24}>
          <Card>
            <Space align="center" wrap>
              <Text strong>Select Machine:</Text>
              <Select
                value={activeMachine}
                onChange={setActiveMachine}
                options={MACHINES.map((m) => ({ value: m, label: m }))}
                style={{ width: 220 }}
              />
            </Space>

            <Divider />

            {loading ? (
              <div style={{ textAlign: "center", padding: 24 }}>
                <Spin />
              </div>
            ) : error ? (
              <Empty description={error?.message || "Failed to load"} />
            ) : (
              <>
                <Space style={{ marginBottom: 12 }}>
                  <Badge color="green" text={`${employees.length} employee(s)`} />
                  <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                    Add Employee
                  </Button>
                </Space>

                <Table
                  rowKey={(r, i) => `${r.value}-${i}`}
                  size="small"
                  dataSource={employees}
                  columns={columns}
                  pagination={{ pageSize: 8, showSizeChanger: true }}
                />

                {employees.length === 0 && (
                  <Empty style={{ marginTop: 16 }} description="No employees yet" />
                )}
              </>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        title={
          editingIndex == null
            ? `Add Employee — ${activeMachine}`
            : `Edit Employee — ${activeMachine}`
        }
        open={modalOpen}
        onOk={onSubmit}
        onCancel={closeModal}
        okButtonProps={{ loading: saving }}
        destroyOnClose
        maskClosable={false}>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={(vals) => {
            if ("label" in vals) {
              const currentValue = form.getFieldValue("value")
              // Only auto-fill value if user hasn't touched it or it's empty
              if (
                !currentValue ||
                currentValue === camelKeyFromLabel(form.getFieldValue("label"))
              ) {
                form.setFieldsValue({ value: camelKeyFromLabel(vals.label) })
              }
            }
          }}>
          <Form.Item
            label="Label (shown in UI)"
            name="label"
            rules={[{ required: true, message: "Please enter a label" }]}>
            <Input placeholder='e.g., "Aleksejs + Jurijs"' />
          </Form.Item>

          <Form.Item
            label="Value (stored/used)"
            name="value"
            rules={[
              { required: true, message: "Please enter a value" },
              {
                pattern: /^[A-Za-z0-9]+(?:[A-Za-z0-9]+)*$/,
                message: "Use letters/numbers only (e.g., AleksejsAndJurijs)",
              },
            ]}>
            <Input placeholder="e.g., AleksejsAndJurijs" />
          </Form.Item>

          <Text type="secondary">
            Tip: Value auto-generates from the label (CamelCase). You can customize it.
          </Text>
        </Form>
      </Modal>
    </div>
  )
}
